import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { PLAN_DEFAULTS, FEATURE_GROUPS } from "@/pages/Admin/subscriptionFeatureData";

// ── Push Custom Offer Modal ────────────────────────────────────────────────────

function CustomOfferModal({
  siteId,
  onClose,
  onSaved,
}: {
  siteId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [packageId, setPackageId] = useState("2");
  const [interval, setInterval] = useState("monthly");
  const [baseMonthlyPrice, setBaseMonthlyPrice] = useState("");
  const [billedAmount, setBilledAmount] = useState("");
  const [discountPercent, setDiscountPercent] = useState("0");
  const [stripePriceId, setStripePriceId] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!baseMonthlyPrice) {
      displayToast(Severity.Error, "Monthly price is required.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/prices", {
        PackageId: parseInt(packageId, 10),
        Interval: interval,
        BaseMonthlyPrice: parseFloat(baseMonthlyPrice),
        BilledAmount: parseFloat(billedAmount || baseMonthlyPrice),
        DiscountPercent: parseInt(discountPercent, 10) || 0,
        IsActive: true,
        SiteId: parseInt(siteId, 10),
        StripePriceId: stripePriceId.trim() || null,
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          displayToast(Severity.Success, `Custom offer pushed to site ${siteId}`);
          onSaved();
          onClose();
        } else {
          displayToast(Severity.Error, data.error ?? "Failed to save offer.");
        }
      } else {
        displayToast(Severity.Error, "Failed to save offer.");
      }
    } catch {
      displayToast(Severity.Error, "Network error.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Push Custom Offer</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">
            Creates a custom-priced <code>BillingPlanPrice</code> row locked to site <strong>{siteId}</strong>.
            It will appear as the 4th card on the checkout page for this customer.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Package</label>
              <select
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="1">Starter</option>
                <option value="2">Professional</option>
                <option value="3">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Interval</label>
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="biannual">Biannual</option>
                <option value="triennial">Triennial</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Price *</label>
              <input
                type="number"
                value={baseMonthlyPrice}
                onChange={(e) => setBaseMonthlyPrice(e.target.value)}
                placeholder="249.00"
                step="0.01"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Billed Amount</label>
              <input
                type="number"
                value={billedAmount}
                onChange={(e) => setBilledAmount(e.target.value)}
                placeholder="= monthly price if blank"
                step="0.01"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Discount %</label>
              <input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                min="0"
                max="100"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Stripe Price ID</label>
              <input
                type="text"
                value={stripePriceId}
                onChange={(e) => setStripePriceId(e.target.value)}
                placeholder="price_xxx (optional)"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200">
          <SecondaryBlueButton label="Cancel" onClick={onClose} disabled={saving} />
          <PrimaryBlueButton
            label={saving ? "Saving…" : "Push Offer"}
            onClick={handleSave}
            disabled={saving || !baseMonthlyPrice}
          />
        </div>
      </div>
    </div>
  );
}

// ── Billing Contacts Section ──────────────────────────────────────────────────

interface BillingContactRecord {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  title?: string;
  isPrimary: boolean;
  ccOnInvoices: boolean;
  ccOnDunning: boolean;
  notes?: string;
}

const CONTACT_ROLES = ["Billing", "AP", "Executive", "Legal", "Technical", "General"];

function BillingContactsSection({ siteId }: { siteId: string }) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [contacts, setContacts] = useState<BillingContactRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("Billing");
  const [title, setTitle] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [ccOnInvoices, setCcOnInvoices] = useState(true);
  const [ccOnDunning, setCcOnDunning] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(`superadmin/billing/site/${siteId}/contacts`);
      if (resp.ok) setContacts(await resp.json());
    } catch { setContacts([]); } finally { setLoading(false); }
  }, [api.messaging, siteId]);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditId(null);
    setName(""); setEmail(""); setPhone(""); setRole("Billing");
    setTitle(""); setIsPrimary(false); setCcOnInvoices(true);
    setCcOnDunning(false); setNotes("");
    setShowForm(true);
  }

  function openEdit(c: BillingContactRecord) {
    setEditId(c.id);
    setName(c.name); setEmail(c.email); setPhone(c.phone ?? "");
    setRole(c.role ?? "Billing"); setTitle(c.title ?? "");
    setIsPrimary(c.isPrimary); setCcOnInvoices(c.ccOnInvoices);
    setCcOnDunning(c.ccOnDunning); setNotes(c.notes ?? "");
    setShowForm(true);
  }

  async function handleSave() {
    if (!name.trim() || !email.trim()) {
      displayToast(Severity.Error, "Name and email are required.");
      return;
    }
    setSaving(true);
    try {
      const body = { name, email, phone, role, title, isPrimary, ccOnInvoices, ccOnDunning, notes };
      const resp = editId
        ? await api.messaging.put(`superadmin/billing/site/${siteId}/contacts/${editId}`, body)
        : await api.messaging.post(`superadmin/billing/site/${siteId}/contacts`, body);
      const data = await resp.json();
      if (data.success) {
        displayToast(Severity.Success, editId ? "Contact updated." : "Contact added.");
        setShowForm(false);
        await load();
      } else {
        displayToast(Severity.Error, data.error ?? "Failed to save.");
      }
    } catch { displayToast(Severity.Error, "Network error."); }
    finally { setSaving(false); }
  }

  async function handleRemove(id: number, contactName: string) {
    if (!confirm(`Remove ${contactName} from billing contacts?`)) return;
    try {
      const resp = await api.messaging.delete(`superadmin/billing/site/${siteId}/contacts/${id}`);
      const data = await resp.json();
      if (data.success) { displayToast(Severity.Success, "Contact removed."); await load(); }
      else displayToast(Severity.Error, data.error ?? "Failed.");
    } catch { displayToast(Severity.Error, "Network error."); }
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div>
          <p className="text-sm font-semibold text-slate-700">Billing Contacts</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Invoices are sent to the Primary contact. All CC'd contacts receive copies.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cornflower-blue-600 text-white text-xs font-medium hover:bg-cornflower-blue-700"
        >
          <i className="fa-solid fa-plus" /> Add Contact
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="fa-solid fa-spinner fa-spin text-slate-400" />
        </div>
      ) : contacts.length === 0 && !showForm ? (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-slate-400">No billing contacts yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            Without a primary contact, invoices go to the site's admin user.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {contacts.map((c) => (
            <div key={c.id} className="flex items-start gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-800">{c.name}</span>
                  {c.isPrimary && (
                    <span className="text-xs bg-cornflower-blue-100 text-cornflower-blue-700 px-1.5 py-0.5 rounded font-medium">Primary</span>
                  )}
                  {c.role && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c.role}</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                {c.title && <p className="text-xs text-slate-400">{c.title}</p>}
                <div className="flex gap-3 mt-1">
                  {c.ccOnInvoices && <span className="text-xs text-emerald-600">CC on invoices</span>}
                  {c.ccOnDunning && <span className="text-xs text-orange-600">CC on dunning</span>}
                </div>
                {c.notes && <p className="text-xs text-slate-400 italic mt-1">{c.notes}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(c)} className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">Edit</button>
                <button onClick={() => handleRemove(c.id, c.name)} className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            {editId ? "Edit Contact" : "New Contact"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Email *</label>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" type="email"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555-000-0000"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                {CONTACT_ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Title / Department</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Accounts Payable Manager"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
          </div>
          <div className="flex gap-6 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" />
              <span>Primary recipient <span className="text-xs text-slate-400">(replaces site admin as To:)</span></span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={ccOnInvoices} onChange={e => setCcOnInvoices(e.target.checked)} className="rounded" />
              CC on invoices
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={ccOnDunning} onChange={e => setCcOnDunning(e.target.checked)} className="rounded" />
              CC on dunning notices
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save Contact"}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Billing Management Section ─────────────────────────────────────────────────

interface RecurringItem {
  id: number;
  description: string;
  quantity: number;
  unitAmountCents: number;
  totalCents: number;
  itemType: string;
  isActive: boolean;
  sortOrder: number;
  notes?: string;
}

interface BillingInfo {
  billingSubscription?: {
    id: number;
    status: string;
    billingInterval: string;
    baseMonthlyPrice: number;
    billedAmount: number;
    paymentMethod: string;
    manualBillingMode: boolean;
    invoiceDescriptionOverride?: string;
    contractBlobUrl?: string;
    allowSelfUpgrade: boolean;
    revertToPackageIdAtRenewal?: number;
    upgradedFromPackageId?: number;
    nextRenewalDate?: string;
    contractEndDate?: string;
    parallelBillingMode: boolean;
  };
}

function BillingManagementSection({ siteId }: { siteId: string }) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [items, setItems] = useState<RecurringItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<RecurringItem> | null>(null);
  const [expanded, setExpanded] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingContract, setUploadingContract] = useState(false);

  // Editable billing fields
  const sub = billing?.billingSubscription;
  const [manualMode, setManualMode] = useState(false);
  const [descOverride, setDescOverride] = useState("");
  const [allowUpgrade, setAllowUpgrade] = useState(true);

  // Use stable primitive (sub.id) as dep so local edits aren't wiped on every render.
  // Only reset local state when a different subscription loads (id changes).
  const subId = sub?.id;
  useEffect(() => {
    if (sub) {
      setManualMode(sub.manualBillingMode);
      setDescOverride(sub.invoiceDescriptionOverride ?? "");
      setAllowUpgrade(sub.allowSelfUpgrade);
    }
  }, [subId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBilling = useCallback(async () => {
    setLoading(true);
    try {
      const [billingResp, itemsResp] = await Promise.all([
        api.messaging.get(`superadmin/billing/site/${siteId}`),
        api.messaging.get(`superadmin/billing/site/${siteId}/recurring-items`),
      ]);
      if (billingResp.ok) setBilling(await billingResp.json());
      if (itemsResp.ok) setItems(Array.isArray(await itemsResp.clone().json()) ? await itemsResp.json() : []);
    } catch { setBilling(null); } finally { setLoading(false); }
  }, [api, siteId]);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  async function saveBillingSettings() {
    setSaving(true);
    try {
      const resp = await api.messaging.put(`superadmin/billing/site/${siteId}/subscription`, {
        ManualBillingMode: manualMode,
        InvoiceDescriptionOverride: descOverride || null,
        AllowSelfUpgrade: allowUpgrade,
      });
      if (resp.ok) {
        displayToast(Severity.Success, "Billing settings saved.");
        loadBilling();
      } else {
        displayToast(Severity.Error, "Failed to save billing settings.");
      }
    } catch { displayToast(Severity.Error, "Network error."); }
    finally { setSaving(false); }
  }

  async function clearRevertPackage() {
    if (!window.confirm("Clear the scheduled package revert? The account will keep its current (upgraded) package at renewal.")) return;
    setSaving(true);
    try {
      await api.messaging.put(`superadmin/billing/site/${siteId}/subscription`, { ClearRevertPackage: true });
      displayToast(Severity.Success, "Package revert cleared — account will keep current package at renewal.");
      loadBilling();
    } catch { displayToast(Severity.Error, "Network error."); }
    finally { setSaving(false); }
  }

  async function saveItem(item: Partial<RecurringItem>) {
    try {
      const body = {
        Description: item.description,
        Quantity: item.quantity ?? 1,
        UnitAmountCents: item.unitAmountCents ?? 0,
        ItemType: item.itemType ?? "charge",
        IsActive: item.isActive ?? true,
        SortOrder: item.sortOrder ?? 0,
        Notes: item.notes,
      };
      const resp = item.id
        ? await api.messaging.put(`superadmin/billing/site/${siteId}/recurring-items/${item.id}`, body)
        : await api.messaging.post(`superadmin/billing/site/${siteId}/recurring-items`, body);
      const data = await resp.json();
      if (data.success) {
        displayToast(Severity.Success, item.id ? "Item updated." : "Item added.");
        setEditingItem(null);
        loadBilling();
      } else {
        displayToast(Severity.Error, data.error ?? "Failed to save item.");
      }
    } catch { displayToast(Severity.Error, "Network error."); }
  }

  async function deleteItem(id: number) {
    if (!window.confirm("Delete this recurring item? It will no longer appear on future invoices.")) return;
    try {
      const resp = await api.messaging.delete(`superadmin/billing/site/${siteId}/recurring-items/${id}`);
      const data = await resp.json();
      if (data.success) { displayToast(Severity.Success, "Item deleted."); loadBilling(); }
      else displayToast(Severity.Error, "Failed to delete item.");
    } catch { displayToast(Severity.Error, "Network error."); }
  }

  async function handleContractUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingContract(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Use api.messaging so the Bearer token is included (same as all other calls in this file)
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/contract-upload`, formData);
      const data = await resp.json();
      if (data.success) {
        displayToast(Severity.Success, "Contract uploaded.");
        loadBilling();
      } else {
        displayToast(Severity.Error, data.error ?? "Upload failed.");
      }
    } catch { displayToast(Severity.Error, "Upload failed."); }
    finally { setUploadingContract(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  }

  const fmtCents = (c: number) => c < 0 ? `-$${Math.abs(c / 100).toFixed(2)}` : `$${(c / 100).toFixed(2)}`;

  if (loading) return (
    <div className="bg-white rounded-lg shadow-sm p-5 mt-6">
      <div className="flex items-center gap-2 text-sm text-slate-400"><LoadingSpinner />Loading billing…</div>
    </div>
  );

  if (!sub) return (
    <div className="bg-white rounded-lg shadow-sm p-5 mt-6">
      <p className="text-sm text-slate-500">No billing subscription found for this site.
        {" "}<a href={`/admin/billing-financial?siteId=${siteId}`} className="text-blue-600 hover:underline">Create one →</a>
      </p>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm mt-6 overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-file-invoice-dollar text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Billing Management</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            sub.status === "active" ? "bg-green-100 text-green-700" :
            sub.status === "past_due" ? "bg-red-100 text-red-700" :
            "bg-slate-100 text-slate-500"
          }`}>{sub.status}</span>
          {sub.manualBillingMode && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">Manual Billing</span>}
          {sub.parallelBillingMode && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Parallel Mode</span>}
        </div>
        <i className={`fa-solid fa-chevron-${expanded ? "up" : "down"} text-slate-400 text-xs`} />
      </button>

      {expanded && (
        <div className="p-5 space-y-6">

          {/* Help panel */}
          <details className="group bg-cornflower-blue-50 border border-cornflower-blue-100 rounded-xl overflow-hidden">
            <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none hover:bg-cornflower-blue-100 transition">
              <span className="flex items-center gap-2 text-xs font-semibold text-cornflower-blue-700">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How billing management works — click to expand
              </span>
              <svg className="w-3.5 h-3.5 text-cornflower-blue-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="px-4 pb-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-700">
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Manual Billing Mode</p>
                <p className="text-slate-600 leading-relaxed">
                  When ON, the package price is ignored. Only the <strong>Custom Recurring Items</strong> you add below
                  appear on invoices. Use this for custom-priced contracts where the standard plan price doesn't apply.
                  When OFF, Velaro uses the plan price from the package catalog.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Custom Recurring Items</p>
                <p className="text-slate-600 leading-relaxed">
                  Line items added here appear on <em>every</em> renewal invoice for this account.
                  Use <strong>charge</strong> for fees and <strong>credit</strong> for discounts (credits subtract from the total).
                  Example: add a "Platform License" charge of $250 and a "Loyalty Discount" credit of $25.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Invoice Description Override</p>
                <p className="text-slate-600 leading-relaxed">
                  Replaces the default line item description ("Velaro Professional — monthly") on invoices.
                  Use when the customer's contract uses different naming, e.g. "Enterprise Platform License — Annual".
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Contract Upload</p>
                <p className="text-slate-600 leading-relaxed">
                  Upload a signed PDF contract. Stored securely in Azure Blob Storage. The link is visible here
                  and to the customer via their contract portal. Required for annual/multi-year customers —
                  upload before their first invoice is generated.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Allow Self-Upgrade</p>
                <p className="text-slate-600 leading-relaxed">
                  When ON, the customer can upgrade their own plan from within their Velaro account.
                  Turn OFF for locked annual contracts where the plan should not change without your approval.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Revert Package at Renewal</p>
                <p className="text-slate-600 leading-relaxed">
                  Anti-gaming protection. If a customer upgrades within 60 days of their contract end,
                  the system flags the original package here. At renewal, they automatically revert to
                  the original package instead of renewing at the upgraded price.
                  Clear this field if you intentionally approved the upgrade.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-cornflower-blue-100 p-3 md:col-span-2">
                <p className="font-semibold text-slate-700 uppercase tracking-wide text-[10px] mb-1.5">Parallel Billing Mode (Stax Migration)</p>
                <p className="text-slate-600 leading-relaxed">
                  When ON, Velaro generates invoices and tracks revenue internally but
                  <strong> does not send emails to the customer and does not sync to QuickBooks</strong>.
                  Use this while running Velaro billing in parallel with Staxbills before cutting over.
                  Set <strong>Cutover Date</strong> when you're ready to fully switch — full billing resumes from that date.
                </p>
              </div>
            </div>
          </details>

          {/* Billing snapshot */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Interval", sub.billingInterval],
              ["Base Monthly", `$${sub.baseMonthlyPrice?.toFixed(2)}`],
              ["Billed Amount", `$${sub.billedAmount?.toFixed(2)}`],
              ["Payment Method", sub.paymentMethod],
            ].map(([label, value]) => (
              <div key={label} className="bg-slate-50 rounded-md p-3 border border-slate-100">
                <div className="text-[11px] text-slate-400 uppercase font-semibold tracking-wide">{label}</div>
                <div className="text-sm font-medium text-slate-800 mt-0.5">{value}</div>
              </div>
            ))}
          </div>
          {sub.nextRenewalDate && (
            <p className="text-xs text-slate-500">
              Next renewal: <strong>{new Date(sub.nextRenewalDate).toLocaleDateString()}</strong>
              {sub.contractEndDate && <> · Contract ends: <strong>{new Date(sub.contractEndDate).toLocaleDateString()}</strong></>}
            </p>
          )}

          {/* Anti-gaming alert */}
          {sub.revertToPackageIdAtRenewal && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
              <i className="fa-solid fa-triangle-exclamation text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Late-cycle upgrade detected</p>
                <p className="text-amber-700 text-xs mt-0.5">
                  This account upgraded from package #{sub.upgradedFromPackageId} within 60 days of contract end.
                  At next renewal, the system will revert to package #{sub.revertToPackageIdAtRenewal}.
                  Clear this if you want them to keep the upgraded package at renewal.
                </p>
              </div>
              <button
                onClick={clearRevertPackage}
                className="shrink-0 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 rounded px-2 py-1"
              >
                Clear Revert
              </button>
            </div>
          )}

          {/* Invoice settings */}
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Invoice Settings</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                <div>
                  <div className="text-sm font-medium text-slate-700">Manual Billing Mode</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    When on, invoices use only the recurring items below — not the package catalog price.
                  </div>
                </div>
                <Toggle toggled={manualMode} changeToggle={() => setManualMode(m => !m)} />
              </div>
              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                <div>
                  <div className="text-sm font-medium text-slate-700">Allow Self-Upgrade</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    When off, the upgrade button is hidden in the messaging app.
                  </div>
                </div>
                <Toggle toggled={allowUpgrade} changeToggle={() => setAllowUpgrade(a => !a)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Invoice Description Override</label>
                <input
                  type="text"
                  value={descOverride}
                  onChange={e => setDescOverride(e.target.value)}
                  placeholder='e.g. "Enterprise Contract — $50,000/year per Agreement #2025-014" (leave blank for auto)'
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">If set, replaces the default "Subscription — annual plan" description on all invoices.</p>
              </div>
              <div className="flex justify-end">
                <PrimaryBlueButton label={saving ? "Saving…" : "Save Invoice Settings"} onClick={saveBillingSettings} disabled={saving} />
              </div>
            </div>
          </div>

          {/* Recurring line items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Recurring Line Items</h4>
                <p className="text-xs text-slate-400 mt-0.5">Added to every generated renewal invoice. Use negative amounts for credits/discounts.</p>
              </div>
              <button
                onClick={() => setEditingItem({ itemType: "charge", quantity: 1, isActive: true, sortOrder: items.length })}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-3 py-1.5"
              >
                <i className="fa-solid fa-plus" /> Add Item
              </button>
            </div>

            {items.length === 0 && !editingItem && (
              <div className="text-center py-6 text-sm text-slate-400 border border-dashed border-slate-200 rounded-md">
                No recurring items — using standard package pricing.
              </div>
            )}

            {items.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Description</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Amount/mo</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">Type</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">Active</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map(item => (
                      <tr key={item.id} className={item.isActive ? "" : "opacity-40"}>
                        <td className="px-3 py-2 text-slate-700">{item.description}</td>
                        <td className={`px-3 py-2 text-right font-mono font-medium ${item.unitAmountCents < 0 ? "text-red-600" : "text-slate-800"}`}>
                          {fmtCents(item.unitAmountCents)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            item.itemType === "credit" ? "bg-red-50 text-red-600" :
                            item.itemType === "discount" ? "bg-amber-50 text-amber-600" :
                            "bg-blue-50 text-blue-600"
                          }`}>{item.itemType}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs font-medium ${item.isActive ? "text-green-600" : "text-slate-400"}`}>
                            {item.isActive ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 justify-end">
                            <button onClick={() => setEditingItem(item)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                            <button onClick={() => deleteItem(item.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {items.length > 1 && (
                    <tfoot>
                      <tr className="bg-slate-50 border-t border-slate-200">
                        <td className="px-3 py-2 text-xs font-semibold text-slate-500">Monthly Total</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">
                          {fmtCents(items.filter(i => i.isActive).reduce((s, i) => s + i.totalCents, 0))}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* Inline item editor */}
            {editingItem !== null && (
              <div className="mt-3 p-4 border border-blue-200 bg-blue-50 rounded-md space-y-3">
                <h5 className="text-xs font-semibold text-blue-800">{editingItem.id ? "Edit Item" : "New Item"}</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Description *</label>
                    <input
                      type="text"
                      value={editingItem.description ?? ""}
                      onChange={e => setEditingItem(i => ({ ...i, description: e.target.value }))}
                      placeholder='e.g. "Professional Plan" or "Custom API Integration"'
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Unit Amount (cents) *</label>
                    <input
                      type="number"
                      value={editingItem.unitAmountCents ?? ""}
                      onChange={e => setEditingItem(i => ({ ...i, unitAmountCents: parseInt(e.target.value) || 0 }))}
                      placeholder="-500000 = -$5,000"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {editingItem.unitAmountCents ? fmtCents(editingItem.unitAmountCents as number) : "Enter amount in cents (e.g. 24900 = $249.00)"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                    <select
                      value={editingItem.itemType ?? "charge"}
                      onChange={e => setEditingItem(i => ({ ...i, itemType: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="charge">Charge</option>
                      <option value="credit">Credit</option>
                      <option value="discount">Discount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Notes (internal)</label>
                    <input
                      type="text"
                      value={editingItem.notes ?? ""}
                      onChange={e => setEditingItem(i => ({ ...i, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <Toggle
                      toggled={editingItem.isActive ?? true}
                      changeToggle={() => setEditingItem(i => ({ ...i, isActive: !i?.isActive }))}
                    />
                    <span className="text-sm text-slate-600">Active</span>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <SecondaryBlueButton label="Cancel" onClick={() => setEditingItem(null)} />
                  <PrimaryBlueButton
                    label="Save Item"
                    onClick={() => saveItem(editingItem)}
                    disabled={!editingItem.description || editingItem.unitAmountCents === undefined}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Contract */}
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Contract</h4>
            <div className="flex items-center gap-3 flex-wrap">
              {sub.contractBlobUrl ? (
                <>
                  <a
                    href={sub.contractBlobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-3 py-1.5"
                  >
                    <i className="fa-solid fa-file-pdf" /> View Contract PDF
                  </a>
                  <span className="text-xs text-slate-400">·</span>
                </>
              ) : (
                <span className="text-sm text-slate-400">No contract on file.</span>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingContract}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"
              >
                <i className="fa-solid fa-upload" />
                {uploadingContract ? "Uploading…" : sub.contractBlobUrl ? "Replace Contract" : "Upload Contract PDF"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleContractUpload}
              />
              <a
                href={`/billing/contracts?siteId=${siteId}`}
                className="text-xs text-blue-600 hover:underline"
              >
                View digital contracts →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Billing Contacts */}
      <BillingContactsSection siteId={siteId} />
    </div>
  );
}

export default function SubscriptionSettings({ initialSiteId }: { initialSiteId?: string } = {}) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const [siteIdInput, setSiteIdInput] = useState(initialSiteId ?? searchParams.get("siteId") ?? "");
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<Record<string, any> | null>(null);
  const [siteInfo, setSiteInfo] = useState<{ companyName?: string } | null>(null);
  const [compareTier, setCompareTier] = useState("enterprise");
  const [applyingDefaults, setApplyingDefaults] = useState(false);
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const [showCustomOfferModal, setShowCustomOfferModal] = useState(false);

  const loadSite = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    setSubscription(null);
    setSiteInfo(null);
    try {
      const resp = await api.admin.post("DatabaseTool/AutoVerify", { siteId: parseInt(id) });
      if (!resp.ok) {
        displayToast(Severity.Error, `Site ${id} not found`);
        return;
      }
      const data = await resp.json();
      if (data.subscription) setSubscription(data.subscription);
      if (data.site_info) setSiteInfo({ companyName: data.site_info.CompanyName });
    } catch (err: any) {
      displayToast(Severity.Error, `Failed to load site: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, displayToast]);

  // Auto-load when navigated with ?siteId=
  useEffect(() => {
    const id = searchParams.get("siteId");
    if (id) {
      setSiteIdInput(id);
      loadSite(id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSearch() {
    if (!siteIdInput) return;
    setSearchParams({ siteId: siteIdInput });
    loadSite(siteIdInput);
  }

  async function toggleFeature(feature: string, enabled: boolean) {
    setToggling(prev => ({ ...prev, [feature]: true }));
    try {
      const res = await api.admin.post("DatabaseTool/ToggleFeature", {
        siteId: parseInt(siteIdInput),
        feature,
        enabled
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "Unknown error");
        displayToast(Severity.Error, `Failed to toggle ${feature}: ${msg}`);
        return;
      }
      setSubscription(prev => prev ? { ...prev, [feature]: enabled ? 1 : 0 } : prev);
      displayToast(Severity.Success, `${feature} ${enabled ? "enabled" : "disabled"}`);
    } catch (err: any) {
      displayToast(Severity.Error, `Error: ${err.message}`);
    } finally {
      setToggling(prev => ({ ...prev, [feature]: false }));
    }
  }

  async function handleApplyPlanDefaults() {
    if (!window.confirm(`Reset ALL subscription flags for site ${siteIdInput} to ${compareTier} plan defaults?`)) return;
    setApplyingDefaults(true);
    try {
      const resp = await api.admin.post(`DatabaseTool/apply-plan-defaults?siteId=${siteIdInput}&tier=${compareTier}`, {});
      const result = await resp.json();
      if (result.success) {
        displayToast(Severity.Success, `Applied ${compareTier} defaults to site ${siteIdInput}`);
        loadSite(siteIdInput);
      } else {
        displayToast(Severity.Error, result.error ?? "Failed to apply plan defaults");
      }
    } catch (err: any) {
      displayToast(Severity.Error, `Error: ${err.message}`);
    } finally {
      setApplyingDefaults(false);
    }
  }

  const planDefaults = PLAN_DEFAULTS[compareTier] ?? PLAN_DEFAULTS["basic"];

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Subscription Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Enable or disable feature flags per customer site. Changes take effect immediately — no restart required.
        </p>
      </div>

      {/* Site Lookup */}
      <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Site ID</label>
            <input
              type="number"
              value={siteIdInput}
              onChange={e => setSiteIdInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Enter site ID (e.g. 1032)"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <PrimaryBlueButton
            label={loading ? "Loading…" : "Load Site"}
            onClick={handleSearch}
            disabled={loading || !siteIdInput}
          />
        </div>

        {siteInfo?.companyName && (
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
            <span className="text-sm font-medium text-slate-700">{siteInfo.companyName}</span>
            <span className="text-xs text-slate-400">— Site {siteIdInput}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Feature Groups */}
      {subscription && !loading && (
        <>
          {/* Plan compare header */}
          <div className="bg-white rounded-lg shadow-sm p-5 mb-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-600">Compare to plan:</label>
                <select
                  value={compareTier}
                  onChange={e => setCompareTier(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <button
                  onClick={handleApplyPlanDefaults}
                  disabled={applyingDefaults}
                  className="text-sm font-semibold px-3 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
                >
                  {applyingDefaults ? "Applying…" : "Apply Plan Defaults"}
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-green-100 border border-green-300 inline-block" />
                  Matches plan
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300 inline-block" />
                  Override from plan
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-slate-100 border border-slate-200 inline-block" />
                  Not in plan
                </span>
              </div>
            </div>
          </div>

          {/* Feature group cards */}
          <div className="space-y-5">
            {FEATURE_GROUPS.map(group => (
              <FeatureGroupCard
                key={group.label}
                group={group}
                subscription={subscription}
                planDefaults={planDefaults}
                toggling={toggling}
                onToggle={toggleFeature}
              />
            ))}
          </div>

          {/* Billing management */}
          <BillingManagementSection siteId={siteIdInput} />

          {/* Custom offer + advanced */}
          <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex-1">
              <span className="font-medium">Advanced settings</span> (retention limits, campaign quotas, scraper limits, auth mode) →{" "}
              <a
                href={`/admin/database-tool?siteId=${siteIdInput}`}
                className="text-blue-600 hover:underline"
              >
                Customer Site Tools
              </a>
            </div>
            <button
              onClick={() => setShowCustomOfferModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors shrink-0"
            >
              <i className="fa-solid fa-tag" />
              Push Custom Offer
            </button>
          </div>
        </>
      )}

      {showCustomOfferModal && siteIdInput && (
        <CustomOfferModal
          siteId={siteIdInput}
          onClose={() => setShowCustomOfferModal(false)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}

function FeatureGroupCard({
  group,
  subscription,
  planDefaults,
  toggling,
  onToggle
}: {
  group: { label: string; features: { key: string; label: string }[] };
  subscription: Record<string, any>;
  planDefaults: Record<string, boolean>;
  toggling: Record<string, boolean>;
  onToggle: (feature: string, enabled: boolean) => void;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{group.label}</h3>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {group.features.map(feature => {
          const currentVal = subscription[feature.key] === 1 || subscription[feature.key] === true;
          const planDefault = planDefaults[feature.key];
          const isOverride = planDefault !== undefined && currentVal !== planDefault;
          const isInPlan = planDefault === true;
          const isBusy = toggling[feature.key];

          let cardClass = "flex items-center justify-between px-3 py-2.5 border rounded-md transition-colors ";
          if (isOverride) {
            cardClass += "border-amber-300 bg-amber-50";
          } else if (isInPlan) {
            cardClass += "border-green-200 bg-green-50";
          } else {
            cardClass += "border-slate-200 bg-slate-50";
          }

          return (
            <div key={feature.key} className={cardClass}>
              <div className="flex flex-col min-w-0 mr-2">
                <span className={`text-sm leading-snug ${isInPlan || currentVal ? "text-slate-800" : "text-slate-500"}`}>
                  {feature.label}
                </span>
                {isOverride && (
                  <span className="text-[10px] font-bold uppercase text-amber-700 tracking-wide mt-0.5">
                    Override — plan: {planDefault ? "on" : "off"}
                  </span>
                )}
              </div>
              {isBusy ? (
                <span className="text-xs text-slate-400 shrink-0">…</span>
              ) : (
                <Toggle
                  toggled={currentVal}
                  changeToggle={() => onToggle(feature.key, !currentVal)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
